import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, CircularProgress, Typography, Button, Slider, TextField, Paper, Modal, Tooltip } from '@mui/material';
import axios from 'axios';
import * as THREE from 'three';
import { useAtom } from 'jotai';
import { useSearchParams } from 'react-router-dom';

import CONFIG from 'utils/config';
import { useAps } from 'contexts/ApsContext';
import { useWorkspaces } from 'contexts/WorkspacesContext';
import { useLoading } from 'contexts/LoadingContext';
import { getPotreeCameraAtom } from 'store/atoms/potree/getPotreeCamera';
import { setPoreeCameraAtom } from 'store/atoms/virtualTourViewer/setPotreeCameraAtom';
import { useGetScanTransformationQuery } from 'api/scan/queries/useGetScanTransformationQuery';
import { isPotreeFocuesAtom } from 'store/atoms/potree/isPotreeFocusedAtom';
import apsModelSettingsPanelVisibleAtom from 'store/atoms/apsViewer/apsModelSettingsPanelVisibleAtom';
import { ApsFileBrowserModal } from 'components/ApsFileBrowser/ApsFileBrowserModal';

// Hardcoded URN for the model
const HARDCODED_URN = 'dXJuOmFkc2sud2lwcHJvZDpmcy5maWxlOnZmLmNId0ptU254UkZ5X0lUVV81RkRYWUE_dmVyc2lvbj0x';

// Declare global Autodesk variable
declare global {
  interface Window {
    Autodesk: any;
  }
}

const ApsViewer: React.FC = () => {
  const { activeWorkspace } = useWorkspaces();
  const { session, loading: apsLoading, triggerApsSignup } = useAps();
  const { scan1 } = useLoading();
  
  const { data: scanTransformation } = useGetScanTransformationQuery(
    scan1 || ""
  );

  const [queryParams, setSearchParams] = useSearchParams();
  const urnFromQueryParams = queryParams.get('urn');
  const shiftX = parseFloat(queryParams.get('shiftX') || '0');
  const shiftY = parseFloat(queryParams.get('shiftY') || '0');
  const shiftZ = parseFloat(queryParams.get('shiftZ') || '0');
  const scaleFactor = parseFloat(queryParams.get('scaleFactor') || '1');
  const rotationX = parseFloat(queryParams.get('rotationX') || '0');
  const rotationY = parseFloat(queryParams.get('rotationY') || '0');
  const rotationZ = parseFloat(queryParams.get('rotationZ') || '0');
  
  // Keep atom definitions but not using them for syncing
  // const [sharedCamera] = useAtom(getPotreeCameraAtom);
  const [unityCameraPayloadString] = useAtom(setPoreeCameraAtom);
  const [isPotreeFocused, setIsPotreeFocused] = useAtom(isPotreeFocuesAtom);
  

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isModelLoading, setIsModelLoading] = useState(false);
  const [activeUrn, setActiveUrn] = useState<string>(urnFromQueryParams || HARDCODED_URN);
  
  // Control panel state using an atom for global access
  const [showControlPanel, setShowControlPanel] = useAtom(apsModelSettingsPanelVisibleAtom);
  const [shiftXValue, setShiftXValue] = useState<number>(shiftX);
  const [shiftYValue, setShiftYValue] = useState<number>(shiftY);
  const [shiftZValue, setShiftZValue] = useState<number>(shiftZ);
  const [scaleFactorValue, setScaleFactorValue] = useState<number>(scaleFactor);
  
  // Rotation controls (in degrees)
  const [rotationXValue, setRotationXValue] = useState<number>(rotationX);
  const [rotationYValue, setRotationYValue] = useState<number>(rotationY);
  const [rotationZValue, setRotationZValue] = useState<number>(rotationZ);
const [isFilePickerModalOpen, setFilePickerModalOpen] = useState(false);

  const viewerContainerRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<any>(null);
  const isViewerReadyRef = useRef(false);
  
  // Reference for camera handler
  const apsCameraChangeHandlerRef = useRef<((event?: unknown) => void) | null>(null);

  const isApsEnabled = !!activeWorkspace?.featureFlags?.includes('aps-integration');

  // Convert the scanTransformation.scanWrtWorld (2D array) into a THREE.Matrix4
  // const scanMatrix = useMemo(() => {
  //   if (!scanTransformation?.scanWrtWorld) return new THREE.Matrix4();
    
  //   // Assuming scanWrtWorld is a 4x4 array
  //   const m = scanTransformation.scanWrtWorld;
  //   return new THREE.Matrix4().set(
  //     m[0][0], m[0][1], m[0][2], m[0][3],
  //     m[1][0], m[1][1], m[1][2], m[1][3],
  //     m[2][0], m[2][1], m[2][2], m[2][3],
  //     m[3][0], m[3][1], m[3][2], m[3][3]
  //   );
  // }, [scanTransformation]);

  // const scanMatrix = scanTransformation?.scanWrtWorld;

  // console.log("[APS] scanMatrix", scanMatrix)

  // Get APS token from backend using 2-legged auth
  useEffect(() => {
    if (!isApsEnabled) {
      setLoading(false);
      return;
    }

    const getToken = async () => {
      try {
        // First try to get token from the backend directly
        try {
          const response = await axios.get(`${CONFIG.API_URL}/aps/get-token`);
          if (response.data && response.data.access_token) {
            setAccessToken(response.data.access_token);
            setLoading(false);
            return;
          }
        } catch {
          // Continue to session check if backend call fails
        }

        // If backend call fails, check if we have a session
        const access_token = session?.accessToken;

        if (access_token) {
          setAccessToken(access_token);
          setLoading(false);
          return;
        }

        // If we don't have a token and we're not already loading from APS context
        if (!apsLoading) {
          triggerApsSignup();
        }
      } catch {
        setError('Failed to get APS access token. Please check your connection and try again.');
        setLoading(false);
      }
    };

    getToken();
  }, [apsLoading, isApsEnabled, session?.accessToken, triggerApsSignup]);

  const loadModel = (modelUrn: string) => {
    if (!viewerRef.current || !accessToken) return;

    setIsModelLoading(true);
    setError(null);

    const Autodesk = window.Autodesk;
    if (!Autodesk || !Autodesk.Viewing) {
      setError('Autodesk Viewer library not loaded');
      setIsModelLoading(false);
      return;
    }

    try {
      const formattedUrn = modelUrn.startsWith('urn:') ? modelUrn : `urn:${modelUrn}`;

      console.log("APS -1", Autodesk.Viewing.Document.load)

      Autodesk.Viewing.Document.load(
        formattedUrn,
        (doc: any) => {
          try {
            // const viewables = doc.getRoot().search({ type: 'geometry' });

            // if (viewables.length === 0) {
            //   setError('No viewable content found in this model');
            //   setIsModelLoading(false);
            //   return;
            // }

            // const defaultModel = viewables[0];

            // Create a comprehensive transformation matrix using the same approach as real-time updates
            const placementTransform = new THREE.Matrix4();
            
            // Create components
            const position = new THREE.Vector3(0, 0, 0);
            
            const scale = new THREE.Vector3(scaleFactorValue, scaleFactorValue, scaleFactorValue);
            
            // Create rotation quaternion
            const quaternion = new THREE.Quaternion();
            
            if (rotationXValue !== 0 || rotationYValue !== 0 || rotationZValue !== 0) {
              // Using same rotation approach as real-time updates
              quaternion.setFromEuler(new THREE.Euler(
                THREE.MathUtils.degToRad(rotationXValue),
                THREE.MathUtils.degToRad(rotationYValue),
                THREE.MathUtils.degToRad(rotationZValue),
                'XYZ' // Same order as real-time updates
              ));
            }
            
            // Compose the transformation matrix
            placementTransform.compose(position, quaternion, scale);
            
            // Log the transformation being applied
            console.log('[APS] Initial model transform:', { 
              shifts: { x: shiftXValue, y: shiftYValue, z: shiftZValue },
              rotations: { x: rotationXValue, y: rotationYValue, z: rotationZValue },
              scale: scaleFactorValue
            });

            const defaultModel = doc.getRoot().getDefaultGeometry();
            console.log("APS -6", defaultModel)

            console.log("APS -2", viewerRef.current.loadDocumentNode)
            viewerRef.current
              .loadDocumentNode(
                doc, 
                defaultModel, 
                {
                  placementTransform: placementTransform,
                  globalOffset: new THREE.Vector3(shiftXValue, shiftYValue, shiftZValue),
                })
                .then((...args: any[]) => {
                  console.log('[APS] Model loaded successfully', args);
                })
                .catch((error: any[]) => {
                  console.error('[APS] Error loading model:', error);
                });
            setIsModelLoading(false);
          } catch (viewableError: any) {
            setError(`Error loading viewable: ${viewableError.message || 'Unknown error'}`);
            setIsModelLoading(false);
          }
        },
        (_errorCode: any, errorMsg: string) => {
          setError(`Error loading model: ${errorMsg}`);
          setIsModelLoading(false);
        }
      );
    } catch (docLoadError: any) {
      setError(`Failed to load model: ${docLoadError.message || 'Unknown error'}`);
      setIsModelLoading(false);
    }
  };

  const initializeViewer = () => {
    if (!viewerContainerRef.current || !accessToken) return;

    const initializeViewerInstance = () => {
      const Autodesk = window.Autodesk;
      const options = {
        env: 'AutodeskProduction',
        accessToken,
      };

      Autodesk.Viewing.Initializer(options, () => {
        viewerRef.current = new Autodesk.Viewing.GuiViewer3D(
          viewerContainerRef.current,
          {
            profileSettings: {
              viewType: 2,
            }
          }
        );
        const startedCode = viewerRef.current.start();
        viewerRef.current.setTheme('light-theme');

        if (startedCode > 0) {
          setError('Failed to initialize the viewer');
          setLoading(false);
          return;
        }

        try {
          viewerRef.current?.setDisplayUnits?.('m');
        } catch {
          // ignore
        }

        const customProfileSettings = {
            settings: {
                reverseMouseZoomDir: true, // override existing
                // reverseHorizontalLookDirection: true, // override existing
                viewType: 2,
            },
            extensions: {

            }
        };
        const customProfile = new Autodesk.Viewing.Profile(customProfileSettings);
        // Updates viewer settings encapsulated witihn a Profile.
        // This method will also load and unload extensions referenced by the Profile.
        viewerRef.current.setProfile(customProfile);

        // viewerRef.current.loadExtension('Autodesk.BIM360.Minimap').then(
        //   (extension: any) => {
        //     extension.load()
        //     console.log('Minimap extension loaded successfully');
        //   },
        //   (error: any) => {
        //     console.error('Failed to load Minimap extension:', error);
        //   }
        // );

        // Check and log camera type
        try {
          const camera = viewerRef.current?.getCamera?.();
          console.log('[APS] Camera info:', {
            isPerspective: camera?.isPerspective,
            isOrtho: camera?.isOrtho,
            type: camera?.constructor?.name,
            fov: camera?.fov,
            orthoScale: camera?.orthoScale
          });

          console.log('APS Viewer Type',viewerRef.current.viewType)
          
          // UnifiedCamera in APS can be in perspective or orthographic mode
          // The mode is determined by the orthoScale property
          // If orthoScale is 0, it's perspective. If > 0, it's orthographic.
          // if (camera && camera.orthoScale && camera.orthoScale > 0) {
          //   console.log('[APS] Camera is in orthographic mode, setting to perspective');
          //   camera.orthoScale = 0;
          //   camera.isPerspective = true;
          //   viewerRef.current?.impl?.invalidate?.(true, true, true);
          //   console.log('[APS] Set camera to perspective mode (orthoScale = 0)');
          // } else {
          //   console.log('[APS] Camera is already in perspective mode');
          // }
        } catch (error) {
          console.error('[APS] Error setting perspective:', error);
        }

        // Enable first-person navigation mode
        try {
          const nav = viewerRef.current?.navigation;
          if (nav) {
            nav.setIsLocked?.(false);
            nav.setReverseZoomDirection?.(false);
            
            // Enable first-person camera controls
            viewerRef.current?.setFirstPersonToolPopup?.(false);
            
            // console.log('[APS] Enabled first-person navigation mode');
          }
        } catch (error) {
          console.error('[APS] Error setting first-person mode:', error);
        }

        console.log("APS viewerRef.current", viewerRef.current.setGroundShadow(false))
        console.log("APS viewerRef.current", viewerRef.current.setGroundReflection(false))
        // console.log("APS viewerRef.current", viewerRef.current.setNavigationLockSettings())

        // console.log('Available extensions:', viewerRef.current.getLoadedExtensions());

        // viewerRef.current
        //   // .loadExtension('Autodesk.BIM360.Minimap')
        //   .loadExtension('Autodesk.AEC.Minimap3DExtension')
        //   .then((ext1: any) => {
        //     console.log('APS ext1', ext1)
        //     ext1?.load().then((loaded: any) => console.log('APS ext1 loaded', loaded))
        //   })
        //   .catch((error: any) => {
        //     console.error('APS Error loading Minimap extension:', error)
        //   })

        isViewerReadyRef.current = true;

        try {
          const AutodeskViewing = Autodesk?.Viewing;
          if (AutodeskViewing?.CAMERA_CHANGE_EVENT) {
            // Camera change handler - no sync with external sources
            const apsCameraChangeHandler = (event?: unknown) => {
              if (!viewerRef.current) return;
              try {
                const camera = viewerRef.current.getCamera();
                if (!camera) return;
                
                // Log camera position for debugging purposes only
                console.log('[APS] Camera position updated:', camera.position);
              } catch (error) {
                console.error('[APS] Error in camera handler:', error);
              }
            };

            apsCameraChangeHandlerRef.current = apsCameraChangeHandler;
            viewerRef.current.addEventListener(AutodeskViewing.CAMERA_CHANGE_EVENT, apsCameraChangeHandler);
          }

          console.log("APS -3", Autodesk.Viewing)

          // viewerRef.current.addEventListener(Autodesk.Viewing.GEOMETRY_LOADED_EVENT, (event: any) => {
            
            // console.log("APS -4", event)
            
            // const model = event.model;

            // console.log("APS -5", model.getInstanceTree())
            
            // 2. Create your new transformation matrix
            // const matrix = new THREE.Matrix4();
            
            // // Start with scale
            // matrix.makeScale(scaleFactorValue, scaleFactorValue, scaleFactorValue);
            
            // // Apply rotations if present
            // if (rotationXValue !== 0 || rotationYValue !== 0 || rotationZValue !== 0) {
            //   // Create rotation matrix from Euler angles (in degrees)
            //   const rotationMatrix = new THREE.Matrix4().makeRotationFromEuler(
            //     new THREE.Euler(
            //       THREE.MathUtils.degToRad(rotationXValue),
            //       THREE.MathUtils.degToRad(rotationYValue),
            //       THREE.MathUtils.degToRad(rotationZValue),
            //       'XYZ'
            //     )
            //   );
              
            //   // Apply rotation to placement transform
            //   matrix.multiply(rotationMatrix);
            // }
            
            // // Apply translation last
            // matrix.setPosition(new THREE.Vector3(shiftXValue, shiftYValue, shiftZValue));

            // // 3. Apply it to the model
            // model.myData.placementTransform = matrix;

            // 4. Update the viewer to reflect the changes
            // viewerRef.current?.impl.invalidate(true, true, true);
          // });
        } catch {
          // ignore
        }

        loadModel(activeUrn);
      });
    };

    try {
      if (!window.Autodesk) {
        const script = document.createElement('script');
        script.src = 'https://developer.api.autodesk.com/modelderivative/v2/viewers/7.*/viewer3D.min.js';
        script.onload = () => initializeViewerInstance();
        script.onerror = () => {
          setError('Failed to load Autodesk Viewer script');
          setLoading(false);
        };
        document.head.appendChild(script);
      } else {
        initializeViewerInstance();
      }
    } catch {
      setError('Failed to initialize viewer');
      setLoading(false);
    }
  };

  // Initialize the viewer once we have the token
  useEffect(() => {
    if (!isApsEnabled) return;
    if (!accessToken) return;

    initializeViewer();

    const handleResize = () => {
      if (viewerRef.current) {
        viewerRef.current.resize();
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (viewerRef.current) {
        try {
          const Autodesk = window.Autodesk;
          const AutodeskViewing = Autodesk?.Viewing;
          if (AutodeskViewing?.CAMERA_CHANGE_EVENT) {
            const handler = apsCameraChangeHandlerRef.current;
            if (handler) {
              viewerRef.current.removeEventListener(AutodeskViewing.CAMERA_CHANGE_EVENT, handler);
            }
          }
        } catch {
          // ignore
        }
        viewerRef.current.finish();
        viewerRef.current = null;
      }

      isViewerReadyRef.current = false;
    };
  }, [accessToken, isApsEnabled]);

  // handle state sync from url params
  useEffect(() => {
    const nextUrn = urnFromQueryParams || HARDCODED_URN;
    setActiveUrn(nextUrn);
    
    // Log parameters when they change
    console.log('[APS] Parameters updated:', { 
      shift: { x: shiftX, y: shiftY, z: shiftZ },
      scale: scaleFactor,
      rotation: { x: rotationX, y: rotationY, z: rotationZ }
    });
    
    // Update control panel values when URL parameters change
    setShiftXValue(shiftX);
    setShiftYValue(shiftY);
    setShiftZValue(shiftZ);
    setScaleFactorValue(scaleFactor);
    setRotationXValue(rotationX);
    setRotationYValue(rotationY);
    setRotationZValue(rotationZ);
  }, [urnFromQueryParams, shiftX, shiftY, shiftZ, scaleFactor, rotationX, rotationY, rotationZ]);

  // handle model loading when URN changes
  useEffect(() => {
    if (!isViewerReadyRef.current) return;
    if (!viewerRef.current) return;
    if (!activeUrn) return;

    loadModel(activeUrn);
    // Don't include transform values here to avoid reloading the model when they change
  }, [activeUrn]);

  // Camera synchronization code removed

  // Effect to apply transformation changes to the model when shift or scale values change
  useEffect(() => {
    // Only run if viewer and model exist
    if (!viewerRef.current || !viewerRef.current.model) return;
    
    const model = viewerRef.current.model;
    console.log("APS -5", model)
    
    // Detailed debug logging
    console.log('[APS] Applying transformation changes to model:', { 
      shifts: { x: shiftXValue, y: shiftYValue, z: shiftZValue },
      rotations: { x: rotationXValue, y: rotationYValue, z: rotationZValue },
      scale: scaleFactorValue,
      modelId: model.id,
      viewerVersion: viewerRef.current?.version,
      modelTransformAvailable: !!model.setModelTransform
    });
    
      // Create transformation matrix
      const transform = new THREE.Matrix4();
      
      const position = new THREE.Vector3(shiftXValue, shiftYValue, shiftZValue);
      
      const scale = new THREE.Vector3(
        scaleFactorValue, 
        scaleFactorValue, 
        scaleFactorValue
      );
      
      const quaternion = new THREE.Quaternion();
      
      if (rotationXValue !== 0 || rotationYValue !== 0 || rotationZValue !== 0) {
        // Rotations in proper order - testing a different approach
        // Using setFromEuler with degrees converted to radians
        quaternion.setFromEuler(new THREE.Euler(
          THREE.MathUtils.degToRad(rotationXValue),
          THREE.MathUtils.degToRad(rotationYValue),
          THREE.MathUtils.degToRad(rotationZValue),
          'XYZ'
        ));
      }
      
        transform.compose(position, quaternion, scale);
        
        // model.setModelTransform(transform);
        // console.log('[APS] Applied transformation using compose method');
      
      // Update the view to show the transformation
      // viewerRef.current.impl.invalidate(true, true, true);
  }, [shiftXValue, shiftYValue, shiftZValue, rotationXValue, rotationYValue, rotationZValue, scaleFactorValue]);

  // handles camera change from unity
  useEffect(() => {
    console.log("[APS] unityCameraPayloadString", unityCameraPayloadString);
    console.log("[APS] viewerRef.current", viewerRef.current);
    if (!viewerRef.current || !unityCameraPayloadString) return;

    console.log("[APS] entered useEffect", viewerRef.current.profile.settings.viewType);
    // console.log("[APS] Using transform values from state:", { shiftX: shiftXValue, shiftY: shiftYValue, shiftZ: shiftZValue, scaleFactor: scaleFactorValue });
    // This is the only place where we should use the state variables for transformations
    try {
      const camObjFromUnity = JSON.parse(unityCameraPayloadString);
      console.table("[APS] camObjFromUnity", camObjFromUnity?.transformationMatrix);
      
      const newCamera = viewerRef.current.getCamera().clone();
      console.log("[APS] newCamera", newCamera)

      console.log('[APS] Navigation', viewerRef.current.navigation)

      
      const columnMajorMatrix = [
        camObjFromUnity?.transformationMatrix.e00,
        camObjFromUnity?.transformationMatrix.e10,
        camObjFromUnity?.transformationMatrix.e20,
        camObjFromUnity?.transformationMatrix.e30,
        camObjFromUnity?.transformationMatrix.e01,
        camObjFromUnity?.transformationMatrix.e11,
        camObjFromUnity?.transformationMatrix.e21,
        camObjFromUnity?.transformationMatrix.e31,
        camObjFromUnity?.transformationMatrix.e02,
        camObjFromUnity?.transformationMatrix.e12,
        camObjFromUnity?.transformationMatrix.e22,
        camObjFromUnity?.transformationMatrix.e32,
        camObjFromUnity?.transformationMatrix.e03,
        camObjFromUnity?.transformationMatrix.e13,
        camObjFromUnity?.transformationMatrix.e23,
        camObjFromUnity?.transformationMatrix.e33,
      ];

      const trmx = new THREE.Matrix4().fromArray(columnMajorMatrix);
      //console.log("this is the transformation matrix ",transformationMatrix);
      const conversionMatrix = new THREE.Matrix4();
      conversionMatrix.set(1, 0, 0, 0, 0, 0, -1, 0, 0, 1, 0, 0, 0, 0, 0, 1);

      // Compute the inverse of the conversion matrix
      const conversionInv = conversionMatrix.clone().invert();

      // Compose: M' = C * M * C⁻¹
      const result = conversionMatrix
        .clone()
        .multiply(trmx)
        .multiply(conversionInv);

      // Use the transformed position for the camera
      const decomposedPosition = new THREE.Vector3();
      const decomposedQuaternion = new THREE.Quaternion();
      const decomposedScale = new THREE.Vector3();

      result.decompose(
        decomposedPosition,
        decomposedQuaternion,
        decomposedScale
      );

      console.log("[APS] decomposedPosition", new THREE.Vector3(
        decomposedPosition.x,
        decomposedPosition.y,
        decomposedPosition.z
      ));

      console.log("[APS] decomposedQuaternion", decomposedQuaternion);

      console.log("[APS] decomposedScale", decomposedScale);

      const euler = new THREE.Euler();
      euler.setFromQuaternion(decomposedQuaternion, "YZX");

      const degX = THREE.MathUtils.radToDeg(euler.x);
      const degY = THREE.MathUtils.radToDeg(euler.y);
      const degZ = THREE.MathUtils.radToDeg(euler.z);

      // Applying your specific offset logic
      const newDegX = 90 - degX;
      const newDegY = degY;
      const newDegZ = 180 - degZ;

      const newEuler = new THREE.Euler(
          THREE.MathUtils.degToRad(newDegX),
          THREE.MathUtils.degToRad(newDegY),
          THREE.MathUtils.degToRad(newDegZ),
          "YZX"
      );

      const newQuaternion = new THREE.Quaternion().setFromEuler(newEuler);

      const unityScale = Math.sqrt(
        camObjFromUnity.transformationMatrix.e00**2 +
        camObjFromUnity.transformationMatrix.e10**2 +
        camObjFromUnity.transformationMatrix.e20**2
      );

      console.log("[APS] unityScale", unityScale)

      // --- APS VIEWER SPECIFIC APPLICATION ---

    // 1. Get the current APS camera
    const camera = viewerRef.current.navigation.getCamera();

    // 2. Set Position (using the original position from Unity)
    camera.position.set(
      -decomposedPosition.x, 
      decomposedPosition.y, 
      decomposedPosition.z
    );

    // 3. Set Rotation - only using the base Unity rotation
    camera.quaternion.copy(newQuaternion);

    camera.isPerspective = true;
    camera.isOrtho = false;

    // camera.zoom = 1 / unityScale;
    camera.updateProjectionMatrix();

    // 4. Calculate Up Vector and Target for the Viewer Navigation
    // APS needs these to keep the orbit controls working correctly.
    const upVector = new THREE.Vector3(0, 1, 0).applyQuaternion(newQuaternion);
    const worldForward = new THREE.Vector3(0, 0, -1).applyQuaternion(newQuaternion);
    const targetPoint = camera.position.clone().add(worldForward.multiplyScalar(1));

    // 5. Update APS Navigation state
    viewerRef.current?.navigation.setCameraUpVector(upVector);
    viewerRef.current?.navigation.setView(camera.position, targetPoint);

    // 6. Handle FOV
    if (isPotreeFocused.fov) {
        viewerRef.current?.navigation.setVerticalFov(isPotreeFocused.fov);
    }

    
    camera.isPerspective = true;
    camera.isOrtho = false;

    // 7. Commit changes to the renderer
    viewerRef.current?.impl.invalidate(true, true, true);
      
    } catch (err) {
      console.error("APS Camera Sync Error:", err);
    }
  }, [unityCameraPayloadString, viewerRef]);

  // handles token refresh
  const handleRetryGetToken = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(`${CONFIG.API_URL}/aps/get-token`);
      const nextToken = response.data?.access_token;

      if (!nextToken) {
        setError('Failed to get APS access token. Please check your connection and try again.');
        setLoading(false);
        return;
      }

      setAccessToken(nextToken);
      setLoading(false);
    } catch {
      setError('Failed to get APS access token. Please check your connection and try again.');
      setLoading(false);
    }
  };

  if (!isApsEnabled) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h4" sx={{ mb: 2, color: 'primary.main' }}>
            APS Viewer
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            APS integration is not currently enabled for this workspace.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', position: 'relative' }}>
      <ApsFileBrowserModal 
        open={isFilePickerModalOpen}
        onClose={() => setFilePickerModalOpen(false)}
        onFileSelect={(urn) => {
          setActiveUrn(urn);
          setSearchParams(current => {
            const params = new URLSearchParams(current);
            params.set('urn', urn);
            return params;
          });
        }}
      />
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
            Loading APS Viewer...
          </Typography>
        </Box>
      )}

      {error && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 2,
            flexGrow: 1,
          }}
        >
          <Typography color="error">{error}</Typography>
          <Button variant="contained" color="primary" onClick={handleRetryGetToken}>
            Retry
          </Button>
        </Box>
      )}

      {!loading && !error && (
        <Box
          sx={{
            flexGrow: 1,
            width: '100%',
            height: '100%',
            position: 'relative',
          }}
        >
          <Box
            ref={viewerContainerRef}
            sx={{
              width: '100%',
              height: '100%',
              bgcolor: '#F0F0F0',
              border: '1px solid #E0E0E0',
              borderRadius: 1,
              overflow: 'hidden',
              '& .adsk-viewing-viewer': {
                width: '100% !important',
                height: '100% !important',
              },
            }}
            data-testid="aps-viewer-container"
          />
          
          {/* Control Panel - Toggle controlled by toolbar */}
          {showControlPanel && (
            <Paper
              elevation={3}
              sx={{
                position: 'absolute',
                top: '64px',
                right: '16px',
                width: 300,
                height: '75%', // Fixed height
                backgroundColor: 'rgba(20, 20, 20, 0.8)',
                backdropFilter: 'blur(10px)',
                borderRadius: '16px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                zIndex: 100,
                color: '#ffffff',
                display: 'flex',
                flexDirection: 'column' // Flex column layout
              }}
            >
              <Box sx={{ p: 2.5, pb: 2, borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  Transform Controls
                </Typography>
              </Box>
              <Box sx={{ overflowY: 'auto', flexGrow: 1, p: 2.5 }}>

              {/* URN input field */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 0.5, color: '#ffffff' }}>Model URN:</Typography>
                <TextField
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={activeUrn}
                  helperText={
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                      Enter base64-encoded URN or leave empty for default
                    </Typography>
                  }
                  onChange={(e) => {
                    const newUrn = e.target.value;
                    // If the URN is empty, use the hardcoded default
                    const urnToSet = newUrn.trim() === '' ? HARDCODED_URN : newUrn;
                    setActiveUrn(urnToSet);
                    
                    // Update URL parameter
                    setSearchParams(current => {
                      const params = new URLSearchParams(current);
                      if (newUrn.trim() === '') {
                        // Remove URN from URL if empty (will use default)
                        params.delete('urn');
                      } else {
                        params.set('urn', newUrn);
                      }
                      return params;
                    });
                  }}
                  sx={{
                    '& .MuiInputBase-input': {
                      fontSize: '0.75rem',
                      py: 1,
                      color: '#ffffff'
                    },
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.3)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.5)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.7)',
                      },
                    }
                  }}
                />
                <Button 
                  variant="contained" 
                  color="primary" 
                  size="small"
                  onClick={() => setFilePickerModalOpen(true)}
                  sx={{ 
                    mt: 1, 
                    fontSize: '0.75rem',
                    width: '100%',
                    textAlign: 'center'
                  }}
                >
                  Pick File from Autodesk
                </Button>
              </Box>
              
              {/* Divider */}
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#ffffff', mb: 2 }}>
                Position & Scale
              </Typography>
              
              {/* Shift X */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2">Shift X: {shiftXValue}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Slider
                    value={shiftXValue}
                    onChange={(_, value) => {
                      setShiftXValue(value as number);
                      // Update URL parameters
                      setSearchParams(current => {
                        const params = new URLSearchParams(current);
                        params.set('shiftX', value.toString());
                        return params;
                      });
                    }}
                    min={-2000}
                    max={2000}
                    step={0.01}
                    valueLabelDisplay="auto"
                    size="small"
                  />
                  <TextField
                    variant="outlined"
                    size="small"
                    type="number"
                    value={shiftXValue}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value) || 0;
                      setShiftXValue(value);
                      // Update URL parameters
                      setSearchParams(current => {
                        const params = new URLSearchParams(current);
                        params.set('shiftX', value.toString());
                        return params;
                      });
                    }}
                    inputProps={{
                      step: 1,
                      min: -2000,
                      max: 2000,
                      style: { width: '60px' }
                    }}
                  />
                </Box>
              </Box>
              
              {/* Shift Y */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2">Shift Y: {shiftYValue}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Slider
                    value={shiftYValue}
                    onChange={(_, value) => {
                      setShiftYValue(value as number);
                      // Update URL parameters
                      setSearchParams(current => {
                        const params = new URLSearchParams(current);
                        params.set('shiftY', value.toString());
                        return params;
                      });
                    }}
                    min={-2000}
                    max={2000}
                    step={1}
                    valueLabelDisplay="auto"
                    size="small"
                  />
                  <TextField
                    variant="outlined"
                    size="small"
                    type="number"
                    value={shiftYValue}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value) || 0;
                      setShiftYValue(value);
                      // Update URL parameters
                      setSearchParams(current => {
                        const params = new URLSearchParams(current);
                        params.set('shiftY', value.toString());
                        return params;
                      });
                    }}
                    inputProps={{
                      step: 1,
                      min: -2000,
                      max: 2000,
                      style: { width: '60px' }
                    }}
                  />
                </Box>
              </Box>
              
              {/* Shift Z */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2">Shift Z: {shiftZValue}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Slider
                    value={shiftZValue}
                    onChange={(_, value) => {
                      setShiftZValue(value as number);
                      // Update URL parameters
                      setSearchParams(current => {
                        const params = new URLSearchParams(current);
                        params.set('shiftZ', value.toString());
                        return params;
                      });
                    }}
                    min={-2000}
                    max={2000}
                    step={1}
                    valueLabelDisplay="auto"
                    size="small"
                  />
                  <TextField
                    variant="outlined"
                    size="small"
                    type="number"
                    value={shiftZValue}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value) || 0;
                      setShiftZValue(value);
                      // Update URL parameters
                      setSearchParams(current => {
                        const params = new URLSearchParams(current);
                        params.set('shiftZ', value.toString());
                        return params;
                      });
                    }}
                    inputProps={{
                      step: 1,
                      min: -2000,
                      max: 2000,
                      style: { width: '60px' }
                    }}
                  />
                </Box>
              </Box>
              
              {/* Scale Factor */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2">Scale Factor: {scaleFactorValue}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Slider
                    value={scaleFactorValue}
                    onChange={(_, value) => {
                      setScaleFactorValue(value as number);
                      // Update URL parameters
                      setSearchParams(current => {
                        const params = new URLSearchParams(current);
                        params.set('scaleFactor', value.toString());
                        return params;
                      });
                    }}
                    min={0}
                    max={1000}
                    step={1}
                    valueLabelDisplay="auto"
                    size="small"
                  />
                  <TextField
                    variant="outlined"
                    size="small"
                    type="number"
                    value={scaleFactorValue}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value) || 0;
                      setScaleFactorValue(value);
                      // Update URL parameters
                      setSearchParams(current => {
                        const params = new URLSearchParams(current);
                        params.set('scaleFactor', value.toString());
                        return params;
                      });
                    }}
                    inputProps={{
                      step: 1,
                      min: 0,
                      max: 1000,
                      style: { width: '60px' }
                    }}
                  />
                </Box>
              </Box>

              {/* Divider for rotation section */}
              {/* <Box sx={{ 
                height: '2px', 
                backgroundColor: 'rgba(255,255,255,0.3)', 
                my: 2 
              }} /> */}
              
              {/* Rotation Heading with Reset Button */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2" sx={{ 
                  mt: 1, 
                  fontWeight: 'bold',
                  color: '#ffffff' 
                }}>
                  Rotation Controls (degrees)
                </Typography>
                <Button 
                  size="small" 
                  variant="outlined" 
                  sx={{ 
                    fontSize: '0.7rem', 
                    py: 0.5, 
                    px: 1, 
                    color: '#ffffff',
                    borderColor: 'rgba(255,255,255,0.5)',
                    '&:hover': {
                      borderColor: '#ffffff',
                    }
                  }}
                  onClick={() => {
                    // Reset local state
                    setRotationXValue(0);
                    setRotationYValue(0);
                    setRotationZValue(0);
                    
                    // Reset URL parameters
                    setSearchParams(current => {
                      const params = new URLSearchParams(current);
                      params.set('rotationX', '0');
                      params.set('rotationY', '0');
                      params.set('rotationZ', '0');
                      return params;
                    });
                  }}
                >
                  Reset
                </Button>
              </Box>
              
              {/* Rotation X */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ color: '#ffffff' }}>Rotation X: {rotationXValue}°</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Slider
                    value={rotationXValue}
                    onChange={(_, value) => {
                      setRotationXValue(value as number);
                      // Update URL parameters
                      setSearchParams(current => {
                        const params = new URLSearchParams(current);
                        params.set('rotationX', value.toString());
                        return params;
                      });
                    }}
                    min={-180}
                    max={180}
                    step={1}
                    valueLabelDisplay="auto"
                    size="small"
                  />
                  <TextField
                    variant="outlined"
                    size="small"
                    type="number"
                    value={rotationXValue}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value) || 0;
                      setRotationXValue(value);
                      // Update URL parameters
                      setSearchParams(current => {
                        const params = new URLSearchParams(current);
                        params.set('rotationX', value.toString());
                        return params;
                      });
                    }}
                    inputProps={{
                      step: 1,
                      min: -180,
                      max: 180,
                      style: { width: '60px' }
                    }}
                  />
                </Box>
              </Box>
              
              {/* Rotation Y */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ color: '#ffffff' }}>Rotation Y: {rotationYValue}°</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Slider
                    value={rotationYValue}
                    onChange={(_, value) => {
                      setRotationYValue(value as number);
                      // Update URL parameters
                      setSearchParams(current => {
                        const params = new URLSearchParams(current);
                        params.set('rotationY', value.toString());
                        return params;
                      });
                    }}
                    min={-180}
                    max={180}
                    step={1}
                    valueLabelDisplay="auto"
                    size="small"
                  />
                  <TextField
                    variant="outlined"
                    size="small"
                    type="number"
                    value={rotationYValue}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value) || 0;
                      setRotationYValue(value);
                      // Update URL parameters
                      setSearchParams(current => {
                        const params = new URLSearchParams(current);
                        params.set('rotationY', value.toString());
                        return params;
                      });
                    }}
                    inputProps={{
                      step: 1,
                      min: -180,
                      max: 180,
                      style: { width: '60px' }
                    }}
                  />
                </Box>
              </Box>
              
              {/* Rotation Z */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ color: '#ffffff' }}>Rotation Z: {rotationZValue}°</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Slider
                    value={rotationZValue}
                    onChange={(_, value) => {
                      setRotationZValue(value as number);
                      // Update URL parameters
                      setSearchParams(current => {
                        const params = new URLSearchParams(current);
                        params.set('rotationZ', value.toString());
                        return params;
                      });
                    }}
                    min={-180}
                    max={180}
                    step={1}
                    valueLabelDisplay="auto"
                    size="small"
                  />
                  <TextField
                    variant="outlined"
                    size="small"
                    type="number"
                    value={rotationZValue}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value) || 0;
                      setRotationZValue(value);
                      // Update URL parameters
                      setSearchParams(current => {
                        const params = new URLSearchParams(current);
                        params.set('rotationZ', value.toString());
                        return params;
                      });
                    }}
                    inputProps={{
                      step: 1,
                      min: -180,
                      max: 180,
                      style: { width: '60px' }
                    }}
                  />
                </Box>
              </Box>
            </Box> 
            {/* End of scrollable content area */}

            {/* Fixed Footer */}
            <Box sx={{ p: 2.5, pt: 2, borderTop: '1px solid rgba(255, 255, 255, 0.1)', mt: 'auto' }}>
              <Tooltip title="Feature coming soon" placement="top">
                <span>
                  <Button 
                    variant="contained" 
                    fullWidth 
                    disabled
                    sx={{
                      '&.Mui-disabled': {
                        backgroundColor: 'rgba(255, 255, 255, 0.12)',
                        color: 'rgba(255, 255, 255, 0.5)'
                      }
                    }}
                  >
                    Set as Default
                  </Button>
                </span>
              </Tooltip>
            </Box>
            </Paper>
          )}
        </Box>
      )}

      {isModelLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            bgcolor: 'rgba(255,255,255,0.8)',
            p: 3,
            borderRadius: 2,
            zIndex: 10,
          }}
        >
          <CircularProgress />
          <Typography>Loading model...</Typography>
        </Box>
      )}
    </Box>
  );
};



export default ApsViewer;